import type { CalculatorDefinition, SeoDefinition } from '../calculators/types';
import {
  absoluteUrl,
  CALCULATORS_SEO,
  HOME_SEO,
  SITE_DESCRIPTION,
  SITE_NAME,
  SITE_ORIGIN,
  WORKSPACE_SEO,
} from './site';

export type StructuredData = Readonly<Record<string, unknown>>;

export function buildHomeStructuredData(): StructuredData {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        name: SITE_NAME,
        url: SITE_ORIGIN,
        inLanguage: 'ko-KR',
        description: SITE_DESCRIPTION,
      },
      {
        '@type': 'WebApplication',
        name: HOME_SEO.title,
        description: HOME_SEO.description,
        url: absoluteUrl(HOME_SEO.canonicalPath),
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        inLanguage: 'ko-KR',
      },
    ],
  };
}

export function buildProductStructuredData(seo: SeoDefinition): StructuredData {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        name: seo.title,
        description: seo.description,
        url: absoluteUrl(seo.canonicalPath),
        inLanguage: 'ko-KR',
        isPartOf: {
          '@type': 'WebSite',
          name: SITE_NAME,
          url: SITE_ORIGIN,
        },
      },
      {
        '@type': 'WebApplication',
        name: seo.title,
        description: seo.description,
        url: absoluteUrl(seo.canonicalPath),
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        inLanguage: 'ko-KR',
      },
    ],
  };
}

export function buildWorkspaceStructuredData(): StructuredData {
  return buildProductStructuredData(WORKSPACE_SEO);
}

export function buildCalculatorDirectoryStructuredData(): StructuredData {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        name: CALCULATORS_SEO.title,
        description: CALCULATORS_SEO.description,
        url: absoluteUrl(CALCULATORS_SEO.canonicalPath),
        inLanguage: 'ko-KR',
        isPartOf: {
          '@type': 'WebSite',
          name: SITE_NAME,
          url: SITE_ORIGIN,
        },
      },
    ],
  };
}

export function buildCalculatorStructuredData(
  definition: CalculatorDefinition,
): StructuredData {
  const canonicalUrl = absoluteUrl(definition.seo.canonicalPath);

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        name: definition.seo.title,
        description: definition.seo.description,
        url: canonicalUrl,
        inLanguage: 'ko-KR',
        isPartOf: {
          '@type': 'WebSite',
          name: SITE_NAME,
          url: SITE_ORIGIN,
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '홈',
            item: absoluteUrl('/'),
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: '계산기',
            item: absoluteUrl('/calculators'),
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: definition.title,
            item: canonicalUrl,
          },
        ],
      },
    ],
  };
}
